// Single source of truth for "are we online?", shared by the useNetworkStatus
// hook (via useSyncExternalStore) and by non-React callers (sync.ts).
//
// ── Governing principle (offline v4): OFFLINE IS PROVEN BY FAILED REQUESTS,
//    NOT INFERRED FROM RADIO STATE. ──────────────────────────────────────────
//
// Interface-state APIs (`navigator.onLine`, NWPathMonitor, @capacitor/network)
// all report CONNECTED whenever a radio interface is up — and a live interface
// is NOT the same as reachable internet:
//   • iOS keeps WiFi "connected" in airplane mode if it was ever re-enabled
//     there (device-verified — TestFlight 3.2.0);
//   • router up / WAN dead, captive portals, and one-bar dead cellular all
//     present as "connected with no internet".
// So radio-UP proves nothing. Radio-DOWN, on the other hand, is definitive: if
// the interface is gone, we are certainly offline, and that's a valid fast
// path to flip offline instantly.
//
// This store therefore fuses three evidence sources:
//   1. Radio state — plugin on native, navigator + events on web. `disconnected`
//      → flip offline immediately (definitive). `connected` → treat only as a
//      HINT: never flip online off the radio; run a probe first.
//   2. Probe — a no-store `fetch('/manifest.json')` with a ~3s abort timeout.
//      Success is the ONLY thing that flips us back online. Failure/timeout →
//      offline. Rate-limited (≤1 / ~5s) and de-duped so bursts of reports or
//      taps collapse to one request.
//   3. Failure reports — `reportNetworkFailure()`, called from real fetch call
//      sites (sync.ts) when a request throws a network TypeError. A report
//      triggers a probe; a confirmed failure flips offline. This is how we
//      catch the "radio says connected but the internet is gone" cases the
//      interface APIs are blind to.
//
// While offline we re-probe on a ~10s interval AND on radio-change events AND on
// window focus/visibility, so recovery is detected without the user doing
// anything — and, per the principle, we only flip back online once a probe
// actually succeeds (this is what stops the airplane-mode-but-WiFi-up lie from
// flapping us green).
//
// The store is a plain module singleton so both the hook and plain functions
// read the same cached boolean synchronously — useSyncExternalStore needs a
// synchronous getSnapshot, and every real signal (plugin getStatus, the probe)
// is async, so we cache the latest verdict and reconcile as evidence arrives.

import { Network } from '@capacitor/network';
import { isNative, hasNativePlugin } from '@/lib/native/is-native';

let initialized = false;
// First paint assumes connectivity (the common case) and reconciles once real
// evidence arrives — mirrors the hook's getServerSnapshot. Note we do NOT probe
// on boot: an optimistic "online" needs no confirmation, so a normal launch
// costs zero extra requests at the JS layer. We only ever spend a probe when
// something gives us a reason to doubt (a radio-up transition out of offline, a
// failure report, or the offline recovery loop).
let online = true;

const listeners = new Set<() => void>();

// ── Simulated offline (demo env only) ───────────────────────────────────────
// A sales demo can't rely on real signal loss, so the demo build carries a
// pill that forces every consumer of this store to see "offline" while the
// network stays perfectly healthy underneath. The override sits at the READ
// boundary only: the real evidence machinery above keeps running untouched,
// so flipping the simulation off hands back the genuine, already-current
// verdict with no re-probe needed.
//
// It is persisted because the offline surface is a real navigation away — the
// flag has to survive a page load or the demo drops back online mid-story.
// Gating to the demo environment lives in the UI (DemoOfflineToggle); this
// module stays a dumb mechanism so the tests can drive it directly.
export const SIMULATED_OFFLINE_KEY = 'mucka_demo_force_offline';

let simulatedOffline = false;

function readSimulatedOffline(): boolean {
  try {
    return window.localStorage.getItem(SIMULATED_OFFLINE_KEY) === '1';
  } catch {
    return false;
  }
}

// ── Probe coordination ──────────────────────────────────────────────────────
/** Abort budget for a single probe. */
const PROBE_TIMEOUT_MS = 3000;
/** Rate limit: at most one probe per this window (bursts collapse to one). */
const PROBE_MIN_INTERVAL_MS = 5000;
/** While offline, re-probe this often to notice recovery hands-free. */
const OFFLINE_REPROBE_MS = 10000;

let lastProbeAt = 0;
let probeInFlight: Promise<boolean> | null = null;
let reprobeTimer: ReturnType<typeof setInterval> | null = null;

function emit(): void {
  for (const l of listeners) l();
}

function setOnline(next: boolean): void {
  if (next === online) return;
  online = next;
  // The re-probe loop only runs while offline (its whole job is spotting
  // recovery). Start it when we go offline, tear it down when we recover.
  if (online) stopReprobeLoop();
  else startReprobeLoop();
  emit();
}

function readNavigator(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

function startReprobeLoop(): void {
  if (reprobeTimer !== null) return;
  if (typeof setInterval === 'undefined') return;
  reprobeTimer = setInterval(() => { void runProbe(); }, OFFLINE_REPROBE_MS);
}

function stopReprobeLoop(): void {
  if (reprobeTimer === null) return;
  clearInterval(reprobeTimer);
  reprobeTimer = null;
}

/**
 * Fire a single reachability probe, subject to the rate limit and concurrency
 * de-dupe. Resolves to the resulting online verdict (or the current value when
 * the call is rate-limited / probing isn't possible). This is the ONLY path
 * that can flip us from offline back to online.
 */
function runProbe(): Promise<boolean> {
  // De-dupe: fold concurrent callers onto the one in-flight request.
  if (probeInFlight) return probeInFlight;
  // Rate limit: a probe within the last window is fresh enough — reuse it.
  const now = Date.now();
  if (now - lastProbeAt < PROBE_MIN_INTERVAL_MS) return Promise.resolve(online);
  if (typeof fetch === 'undefined') return Promise.resolve(online);
  lastProbeAt = now;

  const promise = (async (): Promise<boolean> => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => { try { controller.abort(); } catch { /* noop */ } }, PROBE_TIMEOUT_MS)
      : null;
    try {
      const res = await fetch('/manifest.json', {
        cache: 'no-store',
        signal: controller?.signal,
      });
      const ok = !!res && res.ok;
      setOnline(ok);
      return ok;
    } catch {
      // Network TypeError, abort/timeout, DNS failure — all real evidence.
      setOnline(false);
      return false;
    } finally {
      if (timer) clearTimeout(timer);
      probeInFlight = null;
    }
  })();

  probeInFlight = promise;
  return promise;
}

/** A definitive-offline radio signal — flip offline now, no probe needed. */
function onRadioDown(): void {
  setOnline(false);
}

/** A radio-up hint — proves nothing. Only verify (probe) if we think we're
 *  offline; if we already believe we're online there's nothing to confirm. */
function onRadioUp(): void {
  if (!online) void runProbe();
}

/** Wire focus/visibility so recovery is noticed the moment the app is looked
 *  at again. Gated to the offline state — an online app has no reason to probe
 *  on every tab focus (OfflineSync separately probes for online freshness). */
function wireVisibilityProbe(): void {
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', onRadioUp);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onRadioUp();
    });
  }
}

function initWeb(): void {
  // `offline`/`online` are the browser's radio-state events. Offline is
  // definitive; online is only a hint that gets verified by a probe.
  window.addEventListener('offline', onRadioDown);
  window.addEventListener('online', onRadioUp);
  // A false at boot is a definitive-offline signal; a true stays optimistic.
  if (readNavigator() === false) setOnline(false);
}

// Lazily wire up the right signal sources exactly once, the first time anyone
// asks. Idempotent and safe to call from getSnapshot during render.
function ensureInit(): void {
  if (initialized) return;
  initialized = true;
  if (typeof window === 'undefined') return; // SSR: leave the online default

  // Restore a demo pill left switched on before a reload/navigation.
  simulatedOffline = readSimulatedOffline();

  wireVisibilityProbe();

  // Native with the plugin present → the plugin is the radio-state source.
  if (isNative() && hasNativePlugin('Network')) {
    Network.getStatus()
      .then((status) => { if (!status.connected) setOnline(false); })
      .catch(() => { /* keep the current cached value */ });
    // App-lifetime listener; never removed. Radio-down flips offline; radio-up
    // only triggers a verifying probe.
    void Network.addListener('networkStatusChange', (status) => {
      if (!status.connected) onRadioDown();
      else onRadioUp();
    });
    return;
  }

  // Web, or an old native binary without the plugin → navigator + events.
  initWeb();
}

/**
 * Current connectivity as a synchronously-readable boolean. Used by
 * useNetworkStatus (getSnapshot) and by non-hook callers like the snapshot
 * sync. SSR-safe: returns `true` before any client init has run.
 */
export function getConnectivity(): boolean {
  ensureInit();
  if (simulatedOffline) return false;
  return online;
}

/** Subscribe to connectivity changes. Returns an unsubscribe fn. SSR-safe. */
export function subscribeConnectivity(callback: () => void): () => void {
  ensureInit();
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

/**
 * Report that a real request just failed at the network layer (a fetch
 * TypeError, not an HTTP error status). Triggers a rate-limited probe; if the
 * probe confirms the failure we flip offline. This is the evidence path that
 * catches "radio connected but internet gone" — the case every interface-state
 * API misses. Fire-and-forget; never throws.
 */
export function reportNetworkFailure(): void {
  ensureInit();
  void runProbe();
}

/**
 * Run (or reuse a fresh) reachability probe and resolve to the resulting online
 * verdict. Used by evidence-driven callers that need the ANSWER — e.g. the
 * dead-nav-tap watcher deciding whether to open the saved-work overlay. Subject
 * to the same rate limit / de-dupe as every other probe.
 */
export function probeConnectivity(): Promise<boolean> {
  ensureInit();
  // A simulated-offline demo must answer "offline" to the callers that ASK
  // rather than read — chiefly the dead-nav-tap watcher, which only opens the
  // saved-work overlay when the probe comes back false. Skipping the request
  // also keeps the demo honest: no network chatter while we're pretending
  // there's no network.
  if (simulatedOffline) return Promise.resolve(false);
  return runProbe();
}

/** Is the demo pill currently forcing offline? */
export function isSimulatedOffline(): boolean {
  ensureInit();
  return simulatedOffline;
}

/**
 * Turn simulated offline on or off and tell every subscriber. Persisted so the
 * state survives the navigation into the offline surface. No-op if the value
 * isn't changing, so repeated calls don't churn the React tree.
 */
export function setSimulatedOffline(next: boolean): void {
  ensureInit();
  if (next === simulatedOffline) return;
  simulatedOffline = next;
  try {
    if (next) window.localStorage.setItem(SIMULATED_OFFLINE_KEY, '1');
    else window.localStorage.removeItem(SIMULATED_OFFLINE_KEY);
  } catch {
    // Private mode / storage disabled — the in-memory flag still works for
    // this page, it just won't survive a reload. Not worth failing the demo.
  }
  emit();
}
